'use client';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import Slider, { Settings } from 'react-slick';

import useWindowSize from '@/hooks/useWindowSize';
import { cn } from '@/lib/utils';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './styles.css';

import { VillageArticle } from '@/services/types';
import VillageArticlesSectionCarouselDialog, {
  JOURNAL_PLACEHOLDER_SRC,
} from './CarouselDialog';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import MediaFilterImage from '@/components/Media/MediaFilterImage';

type Props = {
  articles: VillageArticle[];
  dict: IVillageDictionary;
  lang: string;
};

type DialogInfos = {
  image: VillageArticle['article']['journal']['image'] | null;
  title: string;
  description: string;
  url: string | null;
  journalName: string;
};

export type FormatedArticle = VillageArticle & {
  formatedItems: { url: string; title: string; description: string }[];
};

export default function VillageArticlesSectionCarousel({
  articles,
  dict,
  lang,
}: Props) {
  const [dialogInfos, setDialogInfos] = useState<DialogInfos>();
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);
  const { width } = useWindowSize();

  const articlesWithFilteredTranslations = articles.map((article) => {
    const articleInfos = article.article;
    const filteredTranslations = articleInfos.translations.filter(
      (translation) => translation.locale === lang,
    );
    return {
      ...article,
      article: {
        ...articleInfos,
        translations: filteredTranslations,
      },
    };
  });

  const articlesCount = articles.length;

  const settings: Settings = {
    dots: articlesCount > 1,
    infinite: articlesCount > 1,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const [sliderSettings, setSliderSettings] = useState<Settings>(settings);

  useEffect(() => {
    if (width < 768) {
      setSliderSettings(settings);
    }

    if (width >= 768) {
      setSliderSettings({
        ...settings,
        slidesToShow: 2,
        speed: 1000,
        dots: articlesCount > 2,
        infinite: articlesCount > 2,
      });
    }

    if (width >= 1200) {
      setSliderSettings({
        ...settings,
        slidesToShow: 3,
        dots: articlesCount > 3,
        infinite: articlesCount > 3,
      });
    }
    // eslint-disable-next-line
  }, [width]);

  useEffect(() => {
    if (dialogInfos) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('no-scroll');
      root.classList.add('no-scroll');
    } else {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.remove('no-scroll');
      root.classList.remove('no-scroll');
    }
  }, [dialogInfos]);

  return (
    <div className="w-full h-full relative">
      {dialogInfos && (
        <VillageArticlesSectionCarouselDialog
          {...dialogInfos}
          readArticleLabel={dict.articles.readArticle}
          handleClose={() => setDialogInfos(undefined)}
        />
      )}
      <button
        id="va-carousel-previous-button"
        aria-label="Carousel previous button"
        className={cn(
          'rounded-full z-10 text-zinc-400 flex justify-center group items-center bg-white shadow-lg overflow-hidden absolute top-[50%] md:top-[35%] left-[4px] translate-y-[-50%] mt-[-50px] w-11 h-[44px] desktop:w-[64px] desktop:h-[64px] desktop:left-[calc(0%-2.5rem)]',
          {
            hidden: articlesCount === 1,
          },
          {
            'md:hidden': articlesCount === 2,
          },
          {
            'desktop:hidden': articlesCount === 3,
          },
        )}
        type="button"
        onClick={sliderRef?.slickPrev}>
        <FiChevronLeft className="w-6 h-6 desktop:w-8 desktop:h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
      </button>

      <button
        id="va-carousel-next-button"
        aria-label="Carousel next button"
        className={cn(
          'rounded-full z-10 text-zinc-400 flex justify-center group items-center bg-white shadow-lg overflow-hidden absolute top-[50%] md:top-[35%] right-[4px] translate-y-[-50%] mt-[-50px] w-11 h-[44px] desktop:w-[64px] desktop:h-[64px] desktop:right-[calc(0%-2.5rem)]',
          {
            hidden: articlesCount === 1,
          },
          {
            'md:hidden': articlesCount === 2,
          },
          {
            'desktop:hidden': articlesCount === 3,
          },
        )}
        type="button"
        onClick={sliderRef?.slickNext}>
        <FiChevronRight className="w-6 h-6 desktop:w-8 desktop:h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
      </button>

      <Slider
        ref={setSliderRef}
        className={cn('articles-carousel mb-[10px]')}
        {...sliderSettings}>
        {articlesWithFilteredTranslations.map(({ article }, index) => {
          const articleJournal = article.journal;
          const articleJournalImage = articleJournal.image;

          return (
            <div className="article-column" key={index}>
              <div className="text-center">
                {articleJournalImage ? (
                  <MediaFilterImage
                    media={articleJournalImage}
                    filterName="medium_3-2"
                    alt={articleJournal.name}
                    fill
                    className="!relative block w-auto max-h-[40px] mb-[20px] mx-auto align-middle max-w-full h-auto text-center"
                    sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Image
                    src={JOURNAL_PLACEHOLDER_SRC}
                    alt={article.url}
                    fill
                    className="block w-auto max-h-[40px] mb-[20px] mx-auto align-middle max-w-full h-auto text-center"
                    sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                )}
              </div>
              {article.translations.map(({ title, description }, key) => (
                <div
                  key={key}
                  className="mb-2 text-center block overflow-hidden">
                  <span
                    onClick={() =>
                      setDialogInfos({
                        title,
                        description,
                        url: article.url,
                        image: articleJournalImage,
                        journalName: articleJournal
                          ? articleJournal.name
                          : article.url,
                      })
                    }
                    className="article-link">
                    {title}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </Slider>
    </div>
  );
}
